import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import logoSite from "../../../../assets/brand/logo_site.svg";
import { fetchFilmReel, ReelClip } from "./filmReelShare";

/**
 * Variant M — "Studio" (concept preview of a possible future ET mode; iPad-ish).
 * From the user's mockup: NO per-row thumbnails — a transcript list on the left, ONE main
 * player on the right, and a mini timeline under it. Tapping a row previews it in the player;
 * [+] adds it to the timeline; timeline chips reorder/trim (0.1s steps, like the real ET) and
 * "Play all" plays the sequence through the single player. All media is REAL — loaded live
 * from the shared Film Reel. The stitched-export flow is illustrative (the shipping editor's
 * Export does the real stitching); no invented customers or metrics on this page.
 */

const CHERRY = "#d31337";
const INK = "#17121a";
const PAPER = "#f6f2ea";
const F = "Outfit, sans-serif";
const SERIF = "'Playfair Display', Georgia, serif";
const MONO = "'JetBrains Mono', monospace";
const REDUCED = "@media (prefers-reduced-motion: reduce)";

const injectOnce = () => {
  if (typeof document === "undefined" || document.getElementById("cc-studio-fonts")) return;
  const l = document.createElement("link");
  l.id = "cc-studio-fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "cc-studio-kf";
  s.textContent = "@keyframes ccStUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}";
  document.head.appendChild(s);
};

const up = (delay = 0) => ({
  animation: "ccStUp .55s cubic-bezier(.2,.7,.2,1) both", animationDelay: `${delay}s`,
  [REDUCED]: { animation: "none" },
});

type TimelineItem = { key: number; id: number; in: number; out: number; text: string; thumb: string };

const tc = (s: number) => {
  const t = Math.max(0, Math.round(s * 10));
  return `${String(Math.floor(t / 600)).padStart(2, "0")}:${String(Math.floor((t % 600) / 10)).padStart(2, "0")}.${t % 10}`;
};

const stepBtn = {
  cursor: "pointer", width: 20, height: 20, borderRadius: "5px", border: "1px solid rgba(255,255,255,0.22)",
  background: "transparent", color: "#d5d9e6", display: "flex", alignItems: "center", justifyContent: "center",
  p: 0, fontSize: "12px", lineHeight: 1, "&:hover": { borderColor: CHERRY, color: "#fff" },
} as const;

/** Irregular red ink blob (pure CSS — evokes the splat art without copying it). */
const InkBlob: React.FC<{ sx?: any }> = ({ sx }) => (
  <Box sx={{
    position: "absolute", pointerEvents: "none", zIndex: 0,
    background: `radial-gradient(60% 55% at 45% 45%, ${CHERRY} 0%, #a80f2a 68%, transparent 70%)`,
    borderRadius: "58% 42% 55% 45% / 48% 60% 40% 52%", opacity: 0.9, ...sx,
  }} />
);

const StudioConceptPage: React.FC = () => {
  const [clips, setClips] = useState<ReelClip[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [title, setTitle] = useState("Film Reel");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState<"transcript" | "clips">("transcript");
  const [search, setSearch] = useState("");
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [selKey, setSelKey] = useState<number | null>(null);
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [clock, setClock] = useState({ t: 0, d: 0 });
  const [toast, setToast] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const boundsRef = useRef<{ start: number; end: number } | null>(null);
  const seqRef = useRef<{ key: number } | null>(null); // playing item by IDENTITY, never position (see ET e8bcc98)
  const timelineRef = useRef<TimelineItem[]>([]);
  const playGen = useRef(0); // invalidates stale media-event callbacks from earlier plays
  const nextKey = useRef(1);
  const toastTimer = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    injectOnce();
    const tHeld = toastTimer;
    return () => window.clearTimeout(tHeld.current);
  }, []);

  useEffect(() => { timelineRef.current = timeline; }, [timeline]);

  useEffect(() => {
    let cancelled = false;
    fetchFilmReel()
      .then((d) => { if (!cancelled) { setClips(d.clips); setVideoUrl(d.videoUrl); setTitle(d.title); } })
      .catch(() => { if (!cancelled) setErr("Couldn't load the sample reel — try the full editor instead."); });
    return () => { cancelled = true; };
  }, []);

  const say = (m: string) => {
    setToast(m);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3600);
  };

  /* ---------- single-player engine (same seek/stop mechanics as the real ET) ---------- */

  // play() is called SYNCHRONOUSLY in the user-gesture call stack (Safari/iPad rejects
  // unmuted play() deferred into media-event callbacks), then we seek. A generation
  // counter kills stale loadedmetadata callbacks so rapid clicks / a user's pause can
  // never be overridden by an earlier play's leftovers.
  const playRange = (start: number, end: number) => {
    const v = videoRef.current;
    if (!v) return;
    boundsRef.current = { start, end };
    const gen = ++playGen.current;
    try {
      v.play().catch(() => { /* autoplay denied */ });
      if (v.readyState >= 1) {
        v.currentTime = start;
      } else {
        v.preload = "auto";
        v.addEventListener("loadedmetadata", () => {
          if (playGen.current !== gen) return;
          try { v.currentTime = start; } catch (e) { /* ignore */ }
        }, { once: true });
      }
    } catch (e) { /* ignore */ }
  };

  const previewRow = (row: ReelClip) => {
    seqRef.current = null;
    setActiveRowId(row.id);
    playRange(row.start, row.end);
  };

  const playTimelineItem = (item: TimelineItem, seq: boolean) => {
    seqRef.current = seq ? { key: item.key } : null;
    setSelKey(item.key);
    setActiveRowId(item.id);
    playRange(item.in, item.out);
  };

  const playAll = () => {
    const list = timelineRef.current;
    if (!list.length) { say("Add a clip first — tap + on any line."); return; }
    playTimelineItem(list[0], true);
  };

  // shared end-of-clip logic: advance the sequence by the playing item's IDENTITY
  // (reorder/remove mid-playback stays correct), or stop.
  const advanceOrStop = () => {
    const v = videoRef.current;
    const seq = seqRef.current;
    const list = timelineRef.current;
    if (seq) {
      const i = list.findIndex((x) => x.key === seq.key);
      const next = i >= 0 ? list[i + 1] : undefined;
      if (next) { playTimelineItem(next, true); return; }
    }
    if (v) { try { v.pause(); } catch (e) { /* ignore */ } }
    seqRef.current = null;
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    const b = boundsRef.current;
    if (!v || !b) return;
    setClock({ t: Math.max(0, v.currentTime - b.start), d: b.end - b.start });
    if (v.currentTime >= b.end - 0.05) advanceOrStop();
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const b = boundsRef.current;
      if (!b) { playAll(); return; }
      // finished clip: restart it instead of playing one frame and re-pausing
      if (v.currentTime >= b.end - 0.1) { playRange(b.start, b.end); return; }
      v.play().catch(() => { /* ignore */ });
    } else {
      playGen.current++; // a pause invalidates any in-flight play callbacks
      try { v.pause(); } catch (e) { /* ignore */ }
    }
  };

  /* ---------- timeline ops ---------- */

  const addRow = (row: ReelClip) => {
    if (timeline.some((t) => t.id === row.id)) { say("Already on the timeline."); return; }
    const item: TimelineItem = { key: nextKey.current++, id: row.id, in: row.start, out: row.end, text: row.text, thumb: row.thumb };
    setTimeline((t) => [...t, item]);
    setSelKey(item.key);
    say(`Added — ${timeline.length + 1} clip${timeline.length ? "s" : ""} on the timeline`);
  };

  const move = (key: number, dir: -1 | 1) => setTimeline((t) => {
    const i = t.findIndex((x) => x.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= t.length) return t;
    const n = [...t];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const removeItem = (key: number) => {
    setTimeline((t) => t.filter((x) => x.key !== key));
    if (selKey === key) setSelKey(null);
  };

  // 0.1s per tap, ≥0.3s clip — same steps as the real ET's timecode nudge.
  // OUT is capped at the source video's end (an out past the file's end would stall
  // "Play all": the boundary check could never fire).
  const trim = (key: number, edge: "in" | "out", d: number) => setTimeline((t) => t.map((x) => {
    if (x.key !== key) return x;
    if (edge === "in") return { ...x, in: Math.max(0, Math.min(x.out - 0.3, x.in + d)) };
    const vd = videoRef.current?.duration;
    const cap = vd && isFinite(vd) ? vd : Infinity;
    return { ...x, out: Math.max(x.in + 0.3, Math.min(cap, x.out + d)) };
  }));

  const total = timeline.reduce((s, x) => s + (x.out - x.in), 0);
  const selected = timeline.find((x) => x.key === selKey) || null;
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? clips.filter((c) => c.text.toLowerCase().includes(q)) : clips;
  }, [clips, search]);

  /* ---------- render ---------- */

  const leftPanel = (
    <Box sx={{ width: { xs: "100%", md: 330 }, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: { md: "1px solid rgba(255,255,255,0.08)" }, minHeight: 0 }}>
      <Box sx={{ display: "flex", gap: "6px", p: "10px 12px 8px" }}>
        {(["transcript", "clips"] as const).map((k) => (
          <Box key={k} component="button" onClick={() => setTab(k)} sx={{
            cursor: "pointer", border: "none", borderRadius: "8px", px: "12px", py: "6px",
            fontFamily: F, fontWeight: 700, fontSize: "12px",
            color: tab === k ? "#fff" : "#9aa0b4", background: tab === k ? "rgba(255,255,255,0.1)" : "transparent",
          }}>
            {k === "transcript" ? "Transcript" : "Clips"}
            {k === "clips" && timeline.length > 0 && (
              <Box component="span" sx={{ ml: "6px", fontFamily: MONO, fontSize: "9.5px", color: "#fff", background: CHERRY, borderRadius: "999px", px: "6px", py: "1px" }}>{timeline.length}</Box>
            )}
          </Box>
        ))}
      </Box>

      {tab === "transcript" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mx: "12px", mb: "8px", px: "10px", py: "7px", borderRadius: "9px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <SearchIcon sx={{ fontSize: 15, color: "#8d94a8" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search transcript…"
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: "#e6e8f0", fontFamily: F, fontSize: "12px" }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, maxHeight: { xs: "42vh", md: "none" }, pb: "10px", scrollbarWidth: "thin" }}>
        {!clips.length && (
          <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4", textAlign: "center", mt: "30px", px: "20px" }}>
            {err || "Loading the reel…"}
          </Typography>
        )}

        {tab === "transcript" && filteredRows.map((row) => {
          const active = activeRowId === row.id;
          const onTl = timeline.some((t) => t.id === row.id);
          return (
            <Box key={row.id} onClick={() => previewRow(row)} sx={{
              display: "flex", alignItems: "flex-start", gap: "9px", px: "14px", py: "9px", cursor: "pointer",
              borderLeft: active ? `3px solid ${CHERRY}` : "3px solid transparent",
              background: active ? "rgba(211,19,55,0.12)" : "transparent",
              "&:hover": { background: active ? "rgba(211,19,55,0.16)" : "rgba(255,255,255,0.04)" },
            }}>
              <Typography sx={{ fontFamily: MONO, fontSize: "9.5px", color: active ? CHERRY : "#8d94a8", pt: "2px", flexShrink: 0 }}>{tc(row.start).slice(0, 5)}</Typography>
              <Typography sx={{ fontFamily: F, fontSize: "12px", lineHeight: 1.45, color: "#e6e8f0", flex: 1 }}>{row.text}</Typography>
              <Box component="button" title={onTl ? "Already on the timeline" : "Add to timeline"}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); addRow(row); }}
                sx={{
                  cursor: "pointer", flexShrink: 0, width: 22, height: 22, borderRadius: "6px",
                  border: onTl ? `1px solid ${CHERRY}` : "1px solid rgba(255,255,255,0.2)",
                  background: onTl ? `${CHERRY}33` : "transparent", color: onTl ? "#ff8aa5" : "#c2c7d6",
                  fontSize: "13px", lineHeight: 1, p: 0, "&:hover": { borderColor: CHERRY, color: "#fff" },
                }}>{onTl ? "✓" : "+"}</Box>
            </Box>
          );
        })}
        {tab === "transcript" && clips.length > 0 && !filteredRows.length && (
          <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4", textAlign: "center", mt: "24px" }}>No lines match "{search}".</Typography>
        )}

        {tab === "clips" && !timeline.length && (
          <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4", textAlign: "center", mt: "30px", px: "24px" }}>
            Nothing here yet — tap + on a transcript line to start your cut.
          </Typography>
        )}
        {tab === "clips" && timeline.map((item, i) => (
          <Box key={item.key} onClick={() => playTimelineItem(item, false)} sx={{
            display: "flex", alignItems: "center", gap: "8px", px: "14px", py: "8px", cursor: "pointer",
            background: selKey === item.key ? "rgba(211,19,55,0.12)" : "transparent",
            "&:hover": { background: "rgba(255,255,255,0.04)" },
          }}>
            <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: CHERRY, width: 14, flexShrink: 0 }}>{i + 1}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: F, fontSize: "11.5px", lineHeight: 1.35, color: "#e6e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.text}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: "9px", color: "#8d94a8" }}>{tc(item.in)} – {tc(item.out)}</Typography>
            </Box>
            <Box component="button" title="Move up" onClick={(e: React.MouseEvent) => { e.stopPropagation(); move(item.key, -1); }} sx={stepBtn}>↑</Box>
            <Box component="button" title="Move down" onClick={(e: React.MouseEvent) => { e.stopPropagation(); move(item.key, 1); }} sx={stepBtn}>↓</Box>
            <Box component="button" title="Remove" onClick={(e: React.MouseEvent) => { e.stopPropagation(); removeItem(item.key); }} sx={{ ...stepBtn, color: "#8d94a8" }}>✕</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const rightPanel = (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", p: "12px", overflowY: { md: "auto" } }}>
      {/* main player — fixed height on md so mounting the trim bar never squashes it */}
      <Box sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", background: "#000", aspectRatio: { xs: "16 / 9", md: "auto" }, height: { md: 320 }, flexShrink: 0 }}>
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={clips[0]?.thumb || undefined}
            playsInline
            preload="none"
            muted={false}
            onTimeUpdate={onTimeUpdate}
            onEnded={advanceOrStop}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onClick={togglePlay}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", cursor: "pointer" }}
          />
        ) : (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={logoSite} alt="" width={40} style={{ opacity: 0.7 }} />
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "8px" }}>
        <Box component="button" onClick={togglePlay} sx={{
          cursor: "pointer", width: 34, height: 34, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(255,255,255,0.06)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", p: 0,
          "&:hover": { borderColor: CHERRY },
        }}>{isPlaying ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}</Box>
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: "#d5d9e6" }}>
          {tc(clock.t)} / {tc(Math.max(0, clock.d))}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: F, fontSize: "11px", color: "#8d94a8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 260 }}>
          {activeRowId != null ? clips.find((c) => c.id === activeRowId)?.text : "Tap a transcript line to preview it"}
        </Typography>
      </Box>

      {/* mini timeline */}
      <Box sx={{ mt: "14px", borderTop: "1px solid rgba(255,255,255,0.08)", pt: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "8px" }}>
          <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "12.5px", color: "#f3f4f9" }}>Your cut</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: timeline.length ? CHERRY : "#8d94a8" }}>
            {timeline.length ? `${timeline.length} clip${timeline.length === 1 ? "" : "s"} · ${tc(total)}` : "empty"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box component="button" onClick={playAll} sx={{
            cursor: "pointer", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", px: "11px", py: "5px",
            fontFamily: F, fontWeight: 700, fontSize: "11px", color: "#fff", background: "transparent",
            display: "flex", alignItems: "center", gap: "4px", "&:hover": { borderColor: CHERRY },
          }}><PlayArrowIcon sx={{ fontSize: 14 }} /> Play all</Box>
        </Box>

        <Box sx={{ display: "flex", gap: "5px", overflowX: "auto", pb: "6px", minHeight: 62, alignItems: "stretch", scrollbarWidth: "thin" }}>
          {!timeline.length && (
            <Box sx={{ flex: 1, border: "1.5px dashed rgba(255,255,255,0.16)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: F, fontSize: "11px", color: "#8d94a8" }}>Tap + on a line — clips land here in order</Typography>
            </Box>
          )}
          {timeline.map((item) => (
            <Box key={item.key} onClick={() => playTimelineItem(item, false)} sx={{
              position: "relative", flexShrink: 0, width: Math.max(52, Math.round((item.out - item.in) * 26)), height: 56,
              borderRadius: "8px", overflow: "hidden", cursor: "pointer",
              border: selKey === item.key ? `2px solid ${CHERRY}` : "2px solid rgba(255,255,255,0.12)",
              background: item.thumb ? `url(${item.thumb}) center / cover` : "#221c26",
            }}>
              <Box sx={{ position: "absolute", left: 3, bottom: 2, fontFamily: MONO, fontSize: "8px", color: "#fff", background: "rgba(0,0,0,0.55)", borderRadius: "4px", px: "4px", py: "1px" }}>
                {(item.out - item.in).toFixed(1)}s
              </Box>
            </Box>
          ))}
        </Box>

        {/* trim controls for the selected chip — 0.1s steps like the real editor */}
        {selected && (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", mt: "6px", p: "8px 10px", borderRadius: "9px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Typography sx={{ fontFamily: F, fontSize: "11px", color: "#d5d9e6", flex: 1, minWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selected.text}
            </Typography>
            {(["in", "out"] as const).map((edge) => (
              <Box key={edge} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "9px", color: "#8d94a8" }}>{edge.toUpperCase()}</Typography>
                <Box component="button" onClick={() => trim(selected.key, edge, -0.1)} sx={stepBtn}>−</Box>
                <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: edge === "in" ? "#d5d9e6" : "#f5a524", minWidth: 52, textAlign: "center" }}>
                  {tc(edge === "in" ? selected.in : selected.out)}
                </Typography>
                <Box component="button" onClick={() => trim(selected.key, edge, 0.1)} sx={stepBtn}>+</Box>
              </Box>
            ))}
            <Box component="button" onClick={() => move(selected.key, -1)} sx={stepBtn} title="Earlier">←</Box>
            <Box component="button" onClick={() => move(selected.key, 1)} sx={stepBtn} title="Later">→</Box>
            <Box component="button" onClick={() => removeItem(selected.key)} sx={{ ...stepBtn, color: "#8d94a8" }} title="Remove">✕</Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ background: PAPER, minHeight: "calc(100vh - 64px)", position: "relative", overflow: "hidden" }}>
      {/* ink accents */}
      <InkBlob sx={{ width: 190, height: 150, top: 70, right: "12%", opacity: 0.92, display: { xs: "none", md: "block" } }} />
      <InkBlob sx={{ width: 56, height: 48, top: 210, right: "9%", background: `radial-gradient(60% 55% at 45% 45%, ${INK} 0%, ${INK} 66%, transparent 70%)`, display: { xs: "none", md: "block" } }} />
      {[["6%", "84%", 8], ["10%", "78%", 5], ["88%", "30%", 7], ["84%", "26%", 4]].map(([left, top, size], i) => (
        <Box key={i} sx={{ position: "absolute", left, top, width: size, height: size, borderRadius: "50%", background: i % 2 ? INK : CHERRY, opacity: 0.75, pointerEvents: "none", display: { xs: "none", md: "block" } }} />
      ))}

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1160, mx: "auto", px: { xs: "16px", md: "28px" }, py: { xs: "28px", md: "48px" } }}>
        {/* hero */}
        <Box sx={{ mb: { xs: "24px", md: "36px" } }}>
          <Typography sx={{ ...up(0), fontFamily: MONO, fontSize: "10.5px", letterSpacing: "2.5px", color: "#8a6a55", mb: "10px" }}>
            CHERRYCLIPZ STUDIO · CONCEPT PREVIEW
          </Typography>
          <Typography component="h1" sx={{ ...up(0.05), fontFamily: SERIF, fontWeight: 700, fontSize: { xs: "42px", md: "64px" }, lineHeight: 1.05, color: INK, m: 0 }}>
            Cherry-<Box component="span" sx={{ color: CHERRY }}>pick</Box> your{" "}
            <Box component="span" sx={{ fontStyle: "italic" }}>clipz.</Box>
          </Typography>
          <Typography sx={{ ...up(0.1), fontFamily: F, fontSize: { xs: "14.5px", md: "16px" }, lineHeight: 1.6, color: "#4a4148", maxWidth: 560, mt: "14px" }}>
            The spreadsheet for video, in studio form: read the transcript, tap the lines you want,
            and your cut assembles itself on the little timeline — no scrubbing, no giant editor.
          </Typography>
        </Box>

        {/* the studio card */}
        <Box sx={{ ...up(0.16), borderRadius: "18px", background: INK, border: "1px solid rgba(0,0,0,0.35)", boxShadow: "0 30px 70px rgba(23,17,26,0.35)", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "9px", px: "14px", py: "10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <img src={logoSite} alt="" width={18} />
            <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#8d94a8" }}>Project</Typography>
            <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9" }}>{title}</Typography>
            <Box sx={{ flex: 1 }} />
            <Box component="button"
              onClick={() => say(timeline.length
                ? `In the shipping editor, Export stitches these ${timeline.length} clip${timeline.length === 1 ? "" : "s"} (${tc(total)}) into one video — 9:16 and burned captions optional.`
                : "Add clips first — then Export stitches them, in order.")}
              sx={{
                cursor: "pointer", border: "none", borderRadius: "9px", px: "14px", py: "8px",
                display: "flex", alignItems: "center", gap: "6px",
                fontFamily: F, fontWeight: 800, fontSize: "12px", color: "#fff", background: CHERRY,
                "&:hover": { filter: "brightness(1.12)" },
              }}><FileDownloadIcon sx={{ fontSize: 15 }} /> Export {timeline.length ? `${timeline.length} clip${timeline.length === 1 ? "" : "s"}` : "Clip"}</Box>
          </Box>
          {/* xs: player first (column-reverse) so tapping a line doesn't play audio below the fold */}
          <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", md: "row" }, height: { md: 620 } }}>
            {leftPanel}
            {rightPanel}
          </Box>
        </Box>

        {/* feature cards — every claim is the real product's */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "14px", mt: "26px" }}>
          {[
            { t: "Transcript-based selection", d: "Search or scan the transcript to find the exact moment. No more scrubbing for hours." },
            { t: "Find the best moments fast", d: "Black Cherry AI scores every clip's viral potential 0–100 and flags the filler." },
            { t: "Instant clip export", d: "Download any single clip directly, or stitch your picks — 9:16 vertical with burned captions included." },
          ].map((c, i) => (
            <Box key={c.t} sx={{ ...up(0.2 + i * 0.05), background: "#fffdf8", border: "1px solid rgba(23,17,26,0.1)", borderRadius: "14px", p: "16px 18px", boxShadow: "0 8px 24px rgba(23,17,26,0.06)" }}>
              <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "13.5px", color: INK }}>{c.t}</Typography>
              <Typography sx={{ fontFamily: F, fontSize: "12.5px", lineHeight: 1.55, color: "#5a5158", mt: "6px" }}>{c.d}</Typography>
            </Box>
          ))}
        </Box>

        {/* CTAs + honesty */}
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", mt: "30px" }}>
          <Box component="button" onClick={() => { window.location.href = "/?layout=B"; }} sx={{
            cursor: "pointer", border: "none", borderRadius: "11px", px: "20px", py: "12px",
            fontFamily: F, fontWeight: 800, fontSize: "14px", color: "#fff", background: CHERRY,
            "&:hover": { filter: "brightness(1.1)" },
          }}>Try the real editor →</Box>
          <Box component="button" onClick={() => { window.location.href = "/?layout=J"; }} sx={{
            cursor: "pointer", borderRadius: "11px", px: "20px", py: "12px",
            fontFamily: F, fontWeight: 700, fontSize: "14px", color: INK, background: "transparent",
            border: `1.5px solid ${INK}`, "&:hover": { borderColor: CHERRY, color: CHERRY },
          }}>Join the waitlist</Box>
        </Box>
        <Typography sx={{ fontFamily: F, fontSize: "11.5px", lineHeight: 1.6, color: "#8a8288", textAlign: "center", maxWidth: 640, mx: "auto", mt: "22px" }}>
          Concept preview of a possible future editor mode. The transcript, clips, and video above are real —
          loaded live from the shared sample reel — and the 0.1s trims match the shipping editor. The stitched
          export here is illustrative; the real editor's Export does it for real (9:16 + captions included).
        </Typography>
      </Box>

      {toast && (
        <Box sx={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 13000,
          fontFamily: F, fontSize: "13px", fontWeight: 600, color: "#fff",
          background: "rgba(23,17,26,0.97)", border: `1px solid ${CHERRY}88`, borderRadius: "999px",
          px: "18px", py: "10px", boxShadow: "0 10px 34px rgba(0,0,0,0.35)", maxWidth: "88vw",
        }}>{toast}</Box>
      )}
    </Box>
  );
};

export default StudioConceptPage;
